import api from './client';

// Minimal company/department shapes for selects
export interface CompanyOption {
  id: number;
  name: string;
}

export interface DepartmentOption {
  id: number;
  name: string;
}

export interface JobFunctionOption {
  id: number;
  name: string;
}

export interface EmployeeListItem {
  id: number;
  employee_id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  position: string;
  position_display: string;
  department_name: string;
  company_name: string;
  status: string;
  professional_category?: string;
  email?: string;
  phone?: string;
  photo?: string;
  date_of_birth?: string;
  contract_type?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_status?: string;
  salaire_categoriel?: number;
  total_salary?: number | string; // string | number pour compatibilité avec EmployeeDetail
  seniority?: string;
  created_at?: string;
  identity_document?: string;
  birth_certificate?: string;
  other_documents?: string;
}

// Interface détaillée pour la fiche employé
export interface EmployeeDetail extends EmployeeListItem {
  date_of_birth?: string;
  place_of_birth?: string;
  gender?: string;
  father_name?: string;
  mother_name?: string;
  identity_type?: string;
  identity_number?: string;
  identity_valid_until?: string;
  nationality?: string;
  address?: string;
  phone?: string;
  phone_emergency?: string;
  email?: string;
  cnps_number?: string;
  
  // Organisation (IDs et noms)
  company?: number;
  department?: number;
  
  // Contrat
  contract_type?: string;
  contract_signature_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  
  // Position et catégorie
  position_custom?: string;
  professional_category?: string; // aligné avec backend
  
  // Rémunération
  base_salary?: number;
  transport_allowance?: number;
  performance_bonus?: number;
  communication_allowance?: number;
  sur_salaire?: number;
  salaire_categoriel?: number;
  total_salary?: number | string; // aligné avec backend
  work_location?: string;
  
  // Métadonnées fichiers (legacy single-file fields)
  identity_document_size?: string; identity_document_date?: string;
  birth_certificate_size?: string; birth_certificate_date?: string;
  other_documents_size?: string; other_documents_date?: string;
  photo_size?: string; photo_date?: string;
}

// Pièces jointes multi-documents
export type EmployeeFileCategory = 'IDENTITY' | 'BIRTH' | 'CONTRACT' | 'OTHER';
export interface EmployeeFileItem {
  id: number;
  label: string;
  category: EmployeeFileCategory;
  file: string; // URL
  size?: string;
  uploaded_at: string;
}

// Payload aligned with backend EmployeeCreateSerializer
// SEULS last_name et first_name sont obligatoires - tous les autres champs sont optionnels
export interface EmployeeCreatePayload {
  // Identité - OBLIGATOIRES
  last_name: string;
  first_name: string;

  // Identité - OPTIONNELS
  date_of_birth?: string | null; // YYYY-MM-DD
  place_of_birth?: string | null;
  gender?: 'M' | 'F' | null;
  father_name?: string | null;
  mother_name?: string | null;
  photo?: File | null;

  // Pièce d'identité - OPTIONNELS
  identity_type?: 'CNI' | 'PASSEPORT' | 'PERMIS' | 'AUTRE' | null;
  identity_number?: string | null;
  identity_issue_date?: string | null; // YYYY-MM-DD
  identity_valid_until?: string | null; // YYYY-MM-DD

  // Nationalité et domicile - OPTIONNELS
  nationality?: string | null;
  address?: string | null;
  city?: string | null;

  // Contacts - OPTIONNELS
  phone?: string | null;
  phone_emergency?: string | null;
  email?: string | null;

  // Informations professionnelles - OPTIONNELS (accepte ID number ou string pour création dynamique)
  company?: number | string | null;
  department?: number | string | null;
  position?:
    | 'PROMOTRICE'
    | 'TECHNICIEN'
    | 'SALES_REPRESENTATIVE'
    | 'COMPTABLE'
    | 'TRESORIER'
    | 'CHANNEL_REP'
    | 'REGIONAL_MANAGER'
    | 'AREA_SALES'
    | 'CITY_REP'
    | 'SALES_SUPERVISOR'
    | 'OTHER'
    | null;
  position_custom?: string | null;
  job_function?: number | string | null; // Ajouté - accepte ID ou nom
  professional_category?: string | null; // Accepte n'importe quelle catégorie

  // Contrat - OPTIONNELS
  contract_type?: 'CDI' | 'CDD' | 'STAGE' | 'PRESTATION' | null;
  contract_start_date?: string | null; // YYYY-MM-DD
  contract_end_date?: string | null; // YYYY-MM-DD
  contract_signature_date?: string | null; // YYYY-MM-DD

  // Rémunération - OPTIONNELS
  base_salary?: number | null;
  transport_allowance?: number | null;
  performance_bonus?: number | null;
  communication_allowance?: number | null;
  sur_salaire?: number | null;
  salaire_categoriel?: number | null;

  // Sécurité sociale - OPTIONNELS
  cnps_number?: string | null;
  work_location?: string | null;

  // Statut - OPTIONNEL
  status?: 'ACTIF' | 'INACTIF' | 'SUSPENDU' | 'CONGE' | 'TERMINE';
}

export async function fetchCompanies(): Promise<CompanyOption[]> {
  const { data } = await api.get<CompanyOption[]>('employees/companies/?ordering=name');
  return data;
}

export async function fetchDepartments(): Promise<DepartmentOption[]> {
  const { data } = await api.get<DepartmentOption[]>('employees/departments/?ordering=name');
  return data;
}

export async function fetchJobFunctions(): Promise<JobFunctionOption[]> {
  const { data } = await api.get<JobFunctionOption[]>('employees/job_functions/?ordering=name');
  return data;
}

export async function fetchEmployees(): Promise<EmployeeListItem[]> {
  const { data } = await api.get<any>('employees/employees/');
  // Supporte les deux formats: liste simple ou pagination DRF { results: [...] }
  if (Array.isArray(data)) return data as EmployeeListItem[];
  if (data && Array.isArray(data.results)) return data.results as EmployeeListItem[];
  return [];
}

export async function createEmployee(
  payload: EmployeeCreatePayload,
  photo?: File | null,
  identityDoc?: File | null,
  birthCert?: File | null,
  otherDocs?: File | null
) {
  // Utiliser FormData si au moins un fichier est présent
  if (photo || identityDoc || birthCert || otherDocs) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });
    
    // Ajouter les fichiers s'ils existent
    if (photo) formData.append('photo', photo);
    if (identityDoc) formData.append('identity_document', identityDoc);
    if (birthCert) formData.append('birth_certificate', birthCert);
    if (otherDocs) formData.append('other_documents', otherDocs);
    
    const { data } = await api.post('employees/employees/', formData);
    return data;
  } else {
    // JSON simple sans fichiers
    const { data } = await api.post('employees/employees/', payload);
    return data;
  }
}

export async function checkEmailAvailability(email: string): Promise<{ available: boolean; message?: string }> {
  const { data } = await api.get(`employees/employees/check_email/`, { params: { email } });
  return data;
}

export async function checkIdentityAvailability(number: string): Promise<{ available: boolean; message?: string }> {
  const { data } = await api.get(`employees/employees/check_identity/`, { params: { number } });
  return data;
}

// Récupérer les détails complets d'un employé
export async function fetchEmployeeDetail(id: number): Promise<EmployeeDetail> {
  const { data } = await api.get(`employees/employees/${id}/`);
  return data;
}

// Lister les pièces jointes
export async function fetchEmployeeFiles(id: number): Promise<EmployeeFileItem[]> {
  const { data } = await api.get(`employees/employees/${id}/files/`);
  return Array.isArray(data) ? data : [];
}

// Uploader une pièce jointe (multipart)
export async function uploadEmployeeFile(
  id: number,
  file: File,
  options?: { label?: string; category?: EmployeeFileCategory }
): Promise<EmployeeFileItem> {
  const form = new FormData();
  form.append('file', file);
  if (options?.label) form.append('label', options.label);
  if (options?.category) form.append('category', options.category);
  const { data } = await api.post(`employees/employees/${id}/files/`, form);
  return data;
}

// Supprimer une pièce jointe
export async function deleteEmployeeFile(id: number, fileId: number): Promise<void> {
  await api.delete(`employees/employees/${id}/files/${fileId}/`);
}

// Mettre à jour un employé
export async function updateEmployee(
  id: number,
  payload: Partial<EmployeeCreatePayload>,
  photo?: File | null,
  identityDoc?: File | null,
  birthCert?: File | null,
  otherDocs?: File | null
) {
  // Utiliser FormData si au moins un fichier est présent
  if (photo || identityDoc || birthCert || otherDocs) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });
    
    // Ajouter les fichiers s'ils existent
    if (photo) formData.append('photo', photo);
    if (identityDoc) formData.append('identity_document', identityDoc);
    if (birthCert) formData.append('birth_certificate', birthCert);
    if (otherDocs) formData.append('other_documents', otherDocs);
    
    const { data } = await api.patch(`employees/employees/${id}/`, formData);
    return data;
  } else {
    // JSON simple sans fichiers
    const { data } = await api.patch(`employees/employees/${id}/`, payload);
    return data;
  }
}


// Supprimer un employé
export async function deleteEmployee(id: number): Promise<void> {
  await api.delete(`employees/employees/${id}/`);
}

// Exporter les informations d'un employé en PDF
export async function exportEmployeePdf(id: number): Promise<Blob> {
  const { data } = await api.get(`employees/employees/${id}/export_pdf/`, {
    responseType: 'blob'
  });
  return data;
}

// Exporter les informations d'un employé en DOCX
export async function exportEmployeeDocx(id: number): Promise<Blob> {
  const { data } = await api.get(`employees/employees/${id}/export_docx/`, {
    responseType: 'blob'
  });
  return data;
}

// Générer un document DOCX depuis un template .docx (dans media/documents/templates)
export async function generateEmployeeDocFromTemplate(id: number, templateFileName: string): Promise<Blob> {
  // Le backend attend template_path côté serveur; on lui passe un chemin relatif sous MEDIA_ROOT
  const payload = {
    template_path: `media/documents/templates/${templateFileName}`,
  };
  const { data } = await api.post(`employees/employees/${id}/generate_from_template/`, payload, {
    responseType: 'blob',
  });
  return data;
}

export const POSITION_OPTIONS = [
  { value: 'PROMOTRICE', label: 'Promotrice' },
  { value: 'TECHNICIEN', label: 'Technicien' },
  { value: 'SALES_REPRESENTATIVE', label: 'Sales Representative' },
  { value: 'COMPTABLE', label: 'Comptable' },
  { value: 'TRESORIER', label: 'Trésorier' },
  { value: 'CHANNEL_REP', label: 'Channel Rep' },
  { value: 'REGIONAL_MANAGER', label: 'Regional Manager' },
  { value: 'AREA_SALES', label: 'Area Sales' },
  { value: 'CITY_REP', label: 'City Rep' },
  { value: 'SALES_SUPERVISOR', label: 'Sales Supervisor' },
  { value: 'OTHER', label: 'Autre' },
];

export const CATEGORY_OPTIONS = [
  '1A','1B','2','3','4','5','6','7A','7B','8A','8B','8C','9A','9B','10A','10B','10C','11'
].map((c) => ({ value: c, label: `Catégorie ${c}` }));

export const IDENTITY_TYPE_OPTIONS = [
  { value: 'CNI', label: 'CNI' },
  { value: 'PASSEPORT', label: 'Passeport' },
  { value: 'PERMIS', label: 'Permis de Séjour' },
  { value: 'AUTRE', label: 'Autre' },
];

// ===== Templates de documents =====

export interface DocumentTemplate {
  filename: string;
  display_name: string;
  category: string;
  /** Chemin relatif dans media/documents/templates — requis pour lire les champs de saisie. */
  relative_path?: string;
}

export interface TemplatesResponse {
  templates: DocumentTemplate[];
  categories: Record<string, DocumentTemplate[]>;
}

// Récupérer la liste des templates disponibles
export async function fetchDocumentTemplates(): Promise<TemplatesResponse> {
  const { data } = await api.get<TemplatesResponse>('employees/employees/list_templates/');
  return data;
}

// Générer un document à partir d'un template
export async function generateDocumentFromTemplate(
  employeeId: number,
  templateName: string,
  options?: { format?: 'docx' | 'pdf'; extras?: Record<string, string> }
): Promise<Blob> {
  try {
    const { data } = await api.post(
      `employees/employees/${employeeId}/generate_from_template/`,
      { template_name: templateName, extra: options?.extras },
      { responseType: 'blob', params: options?.format ? { format: options.format } : undefined }
    );
    return data;
  } catch (err: any) {
    // Tenter d'extraire un message clair même si le backend renvoie un Blob d'erreur
    const resp = err?.response as { status?: number; data?: any; headers?: Record<string, string> } | undefined;
    let message = 'Erreur lors de la génération du document.';
    if (resp?.status === 401) {
      message = 'Non authentifié. Veuillez vous reconnecter.';
    } else if (resp?.status === 404) {
      // Par défaut: message générique (on affinera après lecture payload)
      message = 'Ressource introuvable (employé ou template).';
    }
    try {
      if (resp?.data instanceof Blob) {
        const text = await resp.data.text();
        try {
          const json = JSON.parse(text);
          message = json.error || json.detail || message;
        } catch {
          if (text) message = text.slice(0, 500);
        }
      } else if (resp?.data && typeof resp.data === 'object') {
        // Préserver notre message 404 personnalisé si seulement 'detail' générique est fourni
        const detail = resp.data.detail;
        const errMsg = resp.data.error;
        if (errMsg) {
          message = errMsg;
        } else if (!(resp?.status === 404 && (detail === 'Not found.' || /introuvable|pas trouvé/i.test(String(detail))))) {
          message = detail || message;
        }
      }
    } catch {
      // ignore parsing errors
    }
    throw new Error(message);
  }
}
